import { useState } from "react";
import BaseModal from "./BaseModal";
import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { useAtelierStore } from "@store/atelierStore";
import type { OrdreIntervention } from "types/ordre";

/**
 * Correction d'une ligne d'intervention déjà posée.
 *
 * Les quatre champs se saisissaient à l'ajout puis devenaient du texte : une ligne
 * importée avec 1 h par défaut alors que le travail en fait trois ne se rattrapait
 * qu'en la supprimant et en la recréant. On reprend ici le formulaire de l'ajout, à
 * l'identique, plutôt que d'éparpiller des champs sur la ligne : une modification est
 * un geste délibéré, qu'on valide ou qu'on annule.
 *
 * Servie par la fiche ordre comme par la modale ordre, pour que les deux disent la
 * même chose.
 */
export default function InterventionEditModal({
  intervention,
  onClose,
  onSave,
}: {
  intervention: OrdreIntervention;
  onClose: () => void;
  onSave: (patch: Partial<OrdreIntervention>) => void;
}) {
  const workers = useAtelierStore((s) => s.workers);
  const workerOptions = [
    { value: "", label: "Non assigné" },
    ...workers.map((w) => ({ value: w.id, label: w.name })),
    // Un mécanicien parti de l'atelier ne doit pas disparaître de la ligne sans
    // prévenir : on garde son identifiant proposable tant qu'on n'en choisit pas
    // un autre.
    ...(intervention.assigned_to && !workers.some((w) => w.id === intervention.assigned_to)
      ? [{ value: intervention.assigned_to, label: intervention.assigned_to }]
      : []),
  ];

  const [label, setLabel] = useState(intervention.label || "");
  const [detail, setDetail] = useState(intervention.detail || "");
  const [estimatedH, setEstimatedH] = useState(String(intervention.estimated_hours ?? ""));
  const [assignedTo, setAssignedTo] = useState(intervention.assigned_to || "");

  const handleSave = () => {
    if (!label.trim()) return;
    onSave({
      label: label.trim(),
      detail: detail.trim() || undefined,
      // La virgule est ce que tape un clavier français ; `parseFloat` ne la lit pas.
      estimated_hours: parseFloat(estimatedH.replace(",", ".")) || 0,
      assigned_to: assignedTo || undefined,
    });
    onClose();
  };

  return (
    <BaseModal
      title="Modifier l'intervention"
      startIcon={<Icon1 name="modify" size={20} />}
      onClose={onClose}
      width="max-w-lg"
    >
      <div className="p-4 flex flex-col gap-3">
        <TextField label="Libellé" value={label} onChange={setLabel} />
        {/* Précision : le symptôme ou le contexte, ce que le client a dit. */}
        <TextField
          label="Précision (optionnel)"
          value={detail}
          onChange={setDetail}
          placeholder="Symptôme, contexte…"
        />
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Durée estimée (h)"
              type="number"
              value={estimatedH}
              onChange={setEstimatedH}
            />
          </div>
          <div className="flex-1">
            <DropDown
              label="Assigné à"
              options={workerOptions}
              value={assignedTo}
              onSelect={(v) => setAssignedTo(String(v))}
            />
          </div>
        </div>
        {intervention.forfait_code && (
          <span className="text-h4 text-neutral-400">
            Forfait ServiceBox {intervention.forfait_code} — le code reste attaché à la ligne.
          </span>
        )}
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label="Enregistrer" onClick={handleSave} disabled={!label.trim()} />
      </div>
    </BaseModal>
  );
}
